// src/context/brandVoice/widgets/RecordListEditor.jsx — the `record_list` editor (v187):
// terminology.preferred (term + note), terminology.avoid (term + reason) and
// messaging.taboo_claims (claim + reason).
//
// ⭐ The columns come from the payload's `record_fields` — ⛔ never hard-coded: the FIRST key
// is the required one (bold, its placeholder carries the "*"), every other key is optional; v200: every cell is an
// auto-growing box. Names and placeholders come from ONE shared
// map, `brandVoice.recordField.<key>` (placeholders stored WHOLE so the Italian gender is
// right — "Nota (facoltativa)", "Motivo (facoltativo)"); an unknown key shows its raw name.
// ⭐ It edits ROWS and hands up a WHOLE new array on every change; an edit SPREADS the row,
// so any other key survives. ⛔ It validates nothing: blank rows and empty values are handled
// by `wireShape.toWire`, the checks run on save, and `problems` only marks a row
// `aria-invalid`. ⛔ No reordering. No real line breaks (ui/GrowingTextarea.jsx).
// ⭐ `lang="en"` (the bilingual wrapper's English copy) suffixes every accessible label.
// ⭐ An EMPTY list shows only the button "+ Aggiungi riga" (decision 8): no sentence — an empty
// note next to an "add" label are two nearly identical texts and read as a bug.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import GrowingTextarea, { blockEnter, oneLine } from '../ui/GrowingTextarea.jsx';
import { ADD, INPUT, REMOVE, ROW } from './listStyles.js';
import { fieldPlaceholder } from './fieldPlaceholder.js';
import { FONT_SANS } from '../../../constants/theme.js';

const str = (v) => (typeof v === 'string' ? v : '');

export default function RecordListEditor({ field, value, onChange, disabled = false, problems = [], lang = 'it' }) {
  const { t } = useTranslation('context');
  const keys = Array.isArray(field?.record_fields) ? field.record_fields : [];
  const rows = Array.isArray(value) ? value : [];
  const suffix = lang === 'en' ? t('brandVoice.list.enSuffix') : '';
  const nameOf = (key) => t(`brandVoice.recordField.${key}.name`, { defaultValue: key });
  // v200: per-field "Termine*: es. …" placeholders (`fieldPlaceholder.js`), the shared recordField one as fallback.
  const placeholderOf = (key) => fieldPlaceholder(t, {
    path: field?.field_path, column: key, fallbackKey: `brandVoice.recordField.${key}.placeholder`, lang,
  });
  const badRows = new Set((problems || []).map((p) => p?.params?.row).filter(Boolean));
  const replace = (index, key, text) => onChange?.(rows.map((r, i) => (
    i === index ? { ...(r && typeof r === 'object' ? r : {}), [key]: text } : r
  )));
  const remove = (index) => onChange?.(rows.filter((_, i) => i !== index));
  const add = () => onChange?.([...rows, Object.fromEntries(keys.map((k) => [k, '']))]);
  const addText = rows.length === 0 ? t('brandVoice.record.addFirst') : t('brandVoice.record.add');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontFamily: FONT_SANS }}>
      {rows.map((row, i) => {
        const n = i + 1;
        const invalid = badRows.has(n);
        const cellLabel = (key) => `${t('brandVoice.record.cellLabel', { field: nameOf(key), row: n })}${suffix}`;
        return (
          // No reordering, so the index is a stable key for the life of a row.
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={ROW}>
            {/* v200: every cell is a growing one-line box that also fits its placeholder; the first is bold. */}
            {keys.map((key, k) => (
              <GrowingTextarea
                key={key}
                aria-label={cellLabel(key)}
                aria-invalid={invalid || undefined}
                placeholder={placeholderOf(key)}
                fitPlaceholder
                value={str(row?.[key])}
                disabled={disabled}
                onKeyDown={blockEnter}
                onChange={(e) => replace(i, key, oneLine(e.target.value))}
                style={k === 0
                  ? { ...INPUT(invalid), flex: '0 1 190px', minWidth: 140, fontWeight: 600 }
                  : { ...INPUT(invalid), flex: '1 1 200px', minWidth: 0 }}
              />
            ))}
            <button
              type="button"
              aria-label={`${t('brandVoice.record.remove', { row: n })}${suffix}`}
              disabled={disabled}
              onClick={() => remove(i)}
              style={REMOVE(disabled)}
            >
              <X size={16} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        );
      })}
      <div>
        <button
          type="button"
          aria-label={suffix ? `${addText}${suffix}` : undefined}
          disabled={disabled}
          onClick={add}
          style={ADD(disabled)}
        >
          {addText}
        </button>
      </div>
    </div>
  );
}
